import React, { useState, useEffect } from 'react';
import { releasesAPI } from '../services/api';
import { format } from 'date-fns';

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

const Releases = () => {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerCounts, setCustomerCounts] = useState({ all: 0, active: 0, expired: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [uploadingReleaseId, setUploadingReleaseId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [sending, setSending] = useState(false);

  // Create form state
  const [createForm, setCreateForm] = useState({
    version: '',
    title: '',
    changelog: '',
    is_published: false
  });

  // Notification form state
  const [notifyForm, setNotifyForm] = useState({
    filter_type: 'all',
    subject: '',
    custom_message: ''
  });

  useEffect(() => {
    fetchReleases();
    fetchCustomerCounts();
  }, []);

  const fetchReleases = async () => {
    setLoading(true);
    try {
      const response = await releasesAPI.list();
      setReleases(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch releases:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerCounts = async () => {
    try {
      const response = await releasesAPI.getCustomerCounts();
      setCustomerCounts(response.data);
    } catch (err) {
      console.error('Failed to fetch customer counts:', err);
    }
  };

  const handleCreateRelease = async (e) => {
    e.preventDefault();
    try {
      await releasesAPI.create(createForm);
      setShowCreateModal(false);
      setCreateForm({ version: '', title: '', changelog: '', is_published: false });
      fetchReleases();
    } catch (err) {
      console.error('Failed to create release:', err);
      alert('Failed to create release: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleFileUpload = async (releaseId, file) => {
    setUploadingReleaseId(releaseId);
    setUploadProgress(0);
    try {
      // Step 1: Get presigned URL from backend
      const urlResponse = await releasesAPI.getUploadUrl(releaseId, file.name, file.type || 'application/octet-stream');
      const { upload_url, file_url } = urlResponse.data;

      // Step 2: Upload directly to S3 using XMLHttpRequest for progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded * 100) / e.total);
            setUploadProgress(percent);
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`S3 upload failed with status ${xhr.status}`));
          }
        });
        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.send(file);
      });

      // Step 3: Confirm upload with backend
      await releasesAPI.confirmUpload(releaseId, {
        file_url: file_url,
        file_name: file.name,
        file_size: file.size
      });

      fetchReleases();
    } catch (err) {
      console.error('Failed to upload file:', err);
      alert('Failed to upload file: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingReleaseId(null);
      setUploadProgress(0);
    }
  };

  const openNotifyModal = (release) => {
    setSelectedRelease(release);
    setNotifyForm({
      filter_type: 'all',
      subject: `New Release: KrossIQ v${release.version}`,
      custom_message: ''
    });
    setShowNotifyModal(true);
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!selectedRelease) return;

    setSending(true);
    try {
      const response = await releasesAPI.sendNotification(selectedRelease.id, notifyForm);
      alert(`Notification sent to ${response.data.sent_count} customers!`);
      setShowNotifyModal(false);
      fetchReleases();
    } catch (err) {
      console.error('Failed to send notification:', err);
      alert('Failed to send notification: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  const getFilterCount = (filterType) => {
    return customerCounts[filterType] || 0;
  };

  const handleDownload = async (releaseId) => {
    try {
      const response = await releasesAPI.getDownloadUrl(releaseId);
      const { download_url } = response.data;
      // Open the presigned URL in a new tab to trigger download
      window.open(download_url, '_blank');
    } catch (err) {
      console.error('Failed to get download URL:', err);
      alert('Failed to download file: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Releases</h1>
          <p className="page-description">Manage software releases and send notifications</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          Create Release
        </button>
      </div>

      {/* Customer Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>{customerCounts.all}</div>
          <div style={{ color: 'var(--text-secondary)' }}>All Customers</div>
        </div>
        <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--success)' }}>{customerCounts.active}</div>
          <div style={{ color: 'var(--text-secondary)' }}>Active License</div>
        </div>
        <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--warning)' }}>{customerCounts.expired}</div>
          <div style={{ color: 'var(--text-secondary)' }}>Expired License</div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-container"><div className="spinner"></div></div>
        ) : releases.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
            No releases yet. Create your first release to get started.
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Title</th>
                  <th>File</th>
                  <th>Size</th>
                  <th>Published</th>
                  <th>Notifications</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {releases.map((release) => (
                  <tr key={release.id}>
                    <td>
                      <span style={{ fontWeight: 600, color: 'var(--primary)' }}>v{release.version}</span>
                    </td>
                    <td>{release.title}</td>
                    <td>
                      {uploadingReleaseId === release.id ? (
                        <div style={{ minWidth: '100px' }}>
                          <div style={{ fontSize: '0.75rem', marginBottom: '4px' }}>{uploadProgress}%</div>
                          <div style={{ background: 'var(--gray-200)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                            <div style={{ background: 'var(--primary)', height: '100%', width: `${uploadProgress}%`, transition: 'width 0.2s' }}></div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {release.file_url ? (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                              {release.file_name}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>No file</span>
                          )}
                          <label style={{ cursor: 'pointer' }}>
                            <input
                              type="file"
                              accept=".exe"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleFileUpload(release.id, e.target.files[0]);
                                }
                              }}
                            />
                            <span className="btn btn-sm btn-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}>
                              {release.file_url ? 'Re-upload' : 'Upload'}
                            </span>
                          </label>
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        {formatFileSize(release.file_size)}
                      </span>
                    </td>
                    <td>
                      {release.is_published ? (
                        <span className="badge badge-success">Published</span>
                      ) : (
                        <span className="badge badge-gray">Draft</span>
                      )}
                    </td>
                    <td>{release.notification_count} sent</td>
                    <td>{format(new Date(release.created_at), 'MMM d, yyyy')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {release.file_url && (
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleDownload(release.id)}
                          >
                            Download
                          </button>
                        )}
                        <button
                          className={`btn btn-sm ${release.is_published ? 'btn-warning' : 'btn-success'}`}
                          onClick={async () => {
                            try {
                              if (release.is_published) {
                                await releasesAPI.unpublish(release.id);
                              } else {
                                await releasesAPI.publish(release.id);
                              }
                              fetchReleases();
                            } catch (err) {
                              alert('Failed to update publish status');
                            }
                          }}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                        >
                          {release.is_published ? 'Unpublish' : 'Publish'}
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => openNotifyModal(release)}
                        >
                          Send Email
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Release Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Release</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateRelease}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Version *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., 1.2.0"
                    value={createForm.version}
                    onChange={(e) => setCreateForm({ ...createForm, version: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Performance Improvements"
                    value={createForm.title}
                    onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Changelog / Release Notes *</label>
                  <textarea
                    className="form-input"
                    rows={5}
                    placeholder="What's new in this release..."
                    value={createForm.changelog}
                    onChange={(e) => setCreateForm({ ...createForm, changelog: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={createForm.is_published}
                      onChange={(e) => setCreateForm({ ...createForm, is_published: e.target.checked })}
                    />
                    Mark as Published
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Release
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {showNotifyModal && selectedRelease && (
        <div className="modal-overlay" onClick={() => setShowNotifyModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Release Notification</h2>
              <button className="modal-close" onClick={() => setShowNotifyModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSendNotification}>
              <div className="modal-body">
                <div style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--gray-100)', borderRadius: '8px' }}>
                  <strong>Release:</strong> v{selectedRelease.version} - {selectedRelease.title}
                </div>

                <div className="form-group">
                  <label className="form-label">Send To *</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.75rem', border: '1px solid var(--gray-200)', borderRadius: '8px', background: notifyForm.filter_type === 'all' ? 'var(--primary-light)' : 'white' }}>
                      <input
                        type="radio"
                        name="filter_type"
                        value="all"
                        checked={notifyForm.filter_type === 'all'}
                        onChange={(e) => setNotifyForm({ ...notifyForm, filter_type: e.target.value })}
                      />
                      <div>
                        <div style={{ fontWeight: 500 }}>All Customers</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{getFilterCount('all')} recipients</div>
                      </div>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.75rem', border: '1px solid var(--gray-200)', borderRadius: '8px', background: notifyForm.filter_type === 'active' ? 'var(--primary-light)' : 'white' }}>
                      <input
                        type="radio"
                        name="filter_type"
                        value="active"
                        checked={notifyForm.filter_type === 'active'}
                        onChange={(e) => setNotifyForm({ ...notifyForm, filter_type: e.target.value })}
                      />
                      <div>
                        <div style={{ fontWeight: 500 }}>Active License Only</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{getFilterCount('active')} recipients</div>
                      </div>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.75rem', border: '1px solid var(--gray-200)', borderRadius: '8px', background: notifyForm.filter_type === 'expired' ? 'var(--primary-light)' : 'white' }}>
                      <input
                        type="radio"
                        name="filter_type"
                        value="expired"
                        checked={notifyForm.filter_type === 'expired'}
                        onChange={(e) => setNotifyForm({ ...notifyForm, filter_type: e.target.value })}
                      />
                      <div>
                        <div style={{ fontWeight: 500 }}>Expired License Only</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{getFilterCount('expired')} recipients</div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Email Subject</label>
                  <input
                    type="text"
                    className="form-input"
                    value={notifyForm.subject}
                    onChange={(e) => setNotifyForm({ ...notifyForm, subject: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Custom Message (optional)</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    placeholder="Add a personal message to include in the email..."
                    value={notifyForm.custom_message}
                    onChange={(e) => setNotifyForm({ ...notifyForm, custom_message: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNotifyModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={sending}>
                  {sending ? 'Sending...' : `Send to ${getFilterCount(notifyForm.filter_type)} Customers`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--gray-200);
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--text-secondary);
          padding: 0;
          line-height: 1;
        }
        .modal-close:hover {
          color: var(--text-primary);
        }
        .modal-body {
          padding: 1.5rem;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid var(--gray-200);
        }
      `}</style>
    </div>
  );
};

export default Releases;
